/**
 * Drop-zone target component for the SIM script drag-and-drop editor.
 *
 * @module components/sim/dragDrop/DropZone
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';

const ACCEPTS = ['sidebarItem', 'component'];

/**
 * DropZone is a functional component that provides a draggable and droppable area.
 *
 * @param {Object} props - The component's properties.
 * @param {Object} props.data - Data associated with the drop zone, used in the drop handler.
 * @param {Function} props.onDrop - Callback function triggered when an item is dropped on the drop zone. Receives the zone's data and the dropped item.
 * @param {boolean} props.isLast - Flag indicating if the drop zone is the last one in a series, allowing for conditional styling or behavior.
 *
 * @returns {JSX.Element} A styled drop zone area with drag-and-drop functionality.
 */
const DropZone = ({ data, onDrop, isLast }) => {
    const [{ isOver, canDrop }, drop] = useDrop({
        accept: ACCEPTS,
        drop: (item) => {
            onDrop(data, item);
        },
        canDrop: (item) => {
            const itemPath = item.path;

            // sidebar items can always be dropped anywhere
            if (!itemPath) {
                return true;
            }

            return true;
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    const isActive = isOver && canDrop;

    return (
        <div
            className={classNames(
                'dropZone',
                { active: isActive, isLast },
            )}
            ref={drop}
        />
    );
};

/**
 * PropType definitions for the DropZone component.
 *
 * This defines the expected types and requirements for the props
 * that can be passed to the DropZone component.
 *
 * @property {function} onDrop - Callback function to handle files dropped into the drop zone.
 * @property {boolean} [disabled] - Optional flag to disable the drop zone. Default is false.
 * @property {string} [className] - Optional CSS class applied to the drop zone container.
 * @property {string} [accept] - Optional string indicating the types of files allowed (e.g., file extensions or MIME types).
 * @property {boolean} [multiple] - Optional flag to allow multiple file uploads. Default is true.
 */
DropZone.propTypes = {
    data: PropTypes.object,
    onDrop: PropTypes.func,
    isLast: PropTypes.bool,
};

export default DropZone;
